import { isElementDefinition, isElementSettings } from './elements/elementDefinition';
import { parseDefinitions } from './elements/renderHelpers';
import { appendStructurePath, formatStructurePath } from './elements/structurePath';
import type {
    ElementDefinition,
    ElementSettingsEditRequest,
    ElementLocation,
    StructurePathActionHelper,
} from './elements/types';
import type { StructurePath } from './elements/structurePath';

const STRUCTURE_FIELD = 'structure';
// Ephemeral editor-only field used to surface dirty state while free-typing
// in AddElementSlot before content is committed into structure.
const PENDING_SLOT_DRAFT_FIELD = '__pendingSlotDraft';

interface FormAdapter {
    getValues(name: string): unknown;
    setValue(name: string, value: unknown, options: { shouldDirty: boolean }): void;
}

type PathUpdater = (value: unknown, path: StructurePath) => unknown;

function assertArray(value: unknown, path: StructurePath): unknown[] {
    if (!Array.isArray(value)) {
        throw new Error(`Expected "${formatStructurePath(path)}" to be an array.`);
    }

    return value;
}

function assertObjectRecord(value: unknown, path: StructurePath): Record<string, unknown> {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error(`Expected "${formatStructurePath(path)}" to be an object.`);
    }

    return value as Record<string, unknown>;
}

function assertIndexInBounds(index: number, length: number, listPath: StructurePath): void {
    if (!Number.isInteger(index) || index < 0 || index >= length) {
        throw new Error(`Index ${index} is out of bounds for "${formatStructurePath(listPath)}".`);
    }
}

function assertInsertIndex(index: number, length: number, listPath: StructurePath): void {
    if (!Number.isInteger(index) || index < 0 || index > length) {
        throw new Error(
            `Insert index ${index} is out of bounds for "${formatStructurePath(listPath)}".`
        );
    }
}

function updateValueAtPath(
    value: unknown,
    path: StructurePath,
    currentPath: StructurePath,
    updater: PathUpdater
): unknown {
    if (!path.length) {
        return updater(value, currentPath);
    }

    const [segment, ...rest] = path;

    if (typeof segment === 'number') {
        const list = assertArray(value, currentPath);
        assertIndexInBounds(segment, list.length, currentPath);
        const nextList = list.slice();
        const nextPath = appendStructurePath(currentPath, segment);
        nextList[segment] = updateValueAtPath(nextList[segment], rest, nextPath, updater);
        return nextList;
    }

    const objectValue = assertObjectRecord(value, currentPath);
    const nextPath = appendStructurePath(currentPath, segment);

    if (!Object.hasOwn(objectValue, segment)) {
        throw new Error(`Missing "${formatStructurePath(nextPath)}" in structure.`);
    }

    return {
        ...objectValue,
        [segment]: updateValueAtPath(objectValue[segment], rest, nextPath, updater),
    };
}

function readValueAtPath(value: unknown, path: StructurePath, currentPath: StructurePath): unknown {
    if (!path.length) {
        return value;
    }

    const [segment, ...rest] = path;

    if (typeof segment === 'number') {
        const list = assertArray(value, currentPath);
        assertIndexInBounds(segment, list.length, currentPath);
        return readValueAtPath(list[segment], rest, appendStructurePath(currentPath, segment));
    }

    const objectValue = assertObjectRecord(value, currentPath);
    const nextPath = appendStructurePath(currentPath, segment);

    if (!Object.hasOwn(objectValue, segment)) {
        throw new Error(`Missing "${formatStructurePath(nextPath)}" in structure.`);
    }

    return readValueAtPath(objectValue[segment], rest, nextPath);
}

function assertElementDefinitionAtPath(value: unknown, path: StructurePath): ElementDefinition {
    if (!isElementDefinition(value)) {
        throw new Error(`Invalid element definition at "${formatStructurePath(path)}".`);
    }

    return value;
}

function assertSettingsPatchAtPath(value: unknown, path: StructurePath): Record<string, unknown> {
    if (!isElementSettings(value)) {
        throw new Error(`Expected "${formatStructurePath(path)}" to be a settings object.`);
    }

    return value;
}

class ReportStructurePathActions implements StructurePathActionHelper {
    private readonly listPath: StructurePath;

    private readonly index: number;

    private readonly siblingCount: number;

    private readonly moveAtPath: ReportStructureController['moveAtPath'];

    private readonly removeAtPath: ReportStructureController['removeAtPath'];

    constructor(controller: ReportStructureController, location: ElementLocation) {
        this.listPath = location.listPath;
        this.index = location.index;
        this.siblingCount = location.siblingCount;
        this.moveAtPath = controller.moveAtPath.bind(controller);
        this.removeAtPath = controller.removeAtPath.bind(controller);
    }

    get canMoveUp(): boolean {
        return this.index > 0;
    }

    get canMoveDown(): boolean {
        return this.index < this.siblingCount - 1;
    }

    moveUp(): void {
        if (!this.canMoveUp) {
            return;
        }

        this.moveAtPath(this.listPath, this.index, this.index - 1);
    }

    moveDown(): void {
        if (!this.canMoveDown) {
            return;
        }

        this.moveAtPath(this.listPath, this.index, this.index + 1);
    }

    remove(): void {
        this.removeAtPath(this.listPath, this.index);
    }
}

export class ReportStructureController {
    private readonly rhfProps: FormAdapter;

    private readonly name: string;

    private requestSettingsEditHandler: ((request: ElementSettingsEditRequest) => void) | null =
        null;

    constructor(rhfProps: FormAdapter, name = STRUCTURE_FIELD) {
        this.rhfProps = rhfProps;
        this.name = name;
    }

    private get rootPath(): StructurePath {
        return [this.name];
    }

    getStructure(): ElementDefinition[] {
        return parseDefinitions(this.rhfProps.getValues(this.name), this.rootPath);
    }

    setStructure(nextStructure: ElementDefinition[]): void {
        parseDefinitions(nextStructure, this.rootPath);
        this.rhfProps.setValue(this.name, nextStructure, { shouldDirty: true });
    }

    setPendingSlotDraft(text: string): void {
        this.rhfProps.setValue(PENDING_SLOT_DRAFT_FIELD, text, { shouldDirty: true });
    }

    clearPendingSlotDraft(): void {
        this.rhfProps.setValue(PENDING_SLOT_DRAFT_FIELD, undefined, { shouldDirty: true });
    }

    updateStructure(
        updater: (current: ElementDefinition[]) => ElementDefinition[]
    ): ElementDefinition[] {
        const current = this.getStructure();
        const next = updater(current);
        this.setStructure(next);
        return next;
    }

    private updateAtPath(path: StructurePath, updater: PathUpdater): ElementDefinition[] {
        return this.updateStructure(
            (structure) =>
                updateValueAtPath(structure, path, this.rootPath, updater) as ElementDefinition[]
        );
    }

    insertAtPath(
        listPath: StructurePath,
        node: ElementDefinition,
        index: number | null = null
    ): void {
        this.updateAtPath(listPath, (list, currentPath) => {
            const nextList = assertArray(list, currentPath).slice();
            const insertIndex = index === null ? nextList.length : index;
            assertInsertIndex(insertIndex, nextList.length, currentPath);
            const insertPath = appendStructurePath(currentPath, insertIndex);
            nextList.splice(insertIndex, 0, assertElementDefinitionAtPath(node, insertPath));
            return nextList;
        });
    }

    removeAtPath(listPath: StructurePath, index: number): void {
        this.updateAtPath(listPath, (list, currentPath) => {
            const nextList = assertArray(list, currentPath).slice();
            assertIndexInBounds(index, nextList.length, currentPath);
            nextList.splice(index, 1);
            return nextList;
        });
    }

    moveAtPath(listPath: StructurePath, fromIndex: number, toIndex: number): void {
        this.updateAtPath(listPath, (list, currentPath) => {
            const nextList = assertArray(list, currentPath).slice();
            assertIndexInBounds(fromIndex, nextList.length, currentPath);
            assertIndexInBounds(toIndex, nextList.length, currentPath);
            if (fromIndex === toIndex) {
                return nextList;
            }

            const [moved] = nextList.splice(fromIndex, 1);
            nextList.splice(toIndex, 0, moved);
            return nextList;
        });
    }

    // TODO(2026-02-10): DRY this with replaceSettingsAtPath via one shared
    // "update settings at element path" helper that accepts merge/replace strategy.
    updateSettingsAtPath(elementPath: StructurePath, patch: Record<string, unknown>): void {
        this.updateAtPath(elementPath, (node, currentPath) => {
            const definition = assertElementDefinitionAtPath(node, currentPath);
            const patchAtPath = assertSettingsPatchAtPath(
                patch,
                appendStructurePath(currentPath, 'settings')
            );

            return {
                ...definition,
                settings: {
                    ...definition.settings,
                    ...patchAtPath,
                },
            };
        });
    }

    getElementAtPath(elementPath: StructurePath): ElementDefinition {
        const absolutePath = elementPath.reduce(
            (currentPath, segment) => appendStructurePath(currentPath, segment),
            this.rootPath
        );

        const node = readValueAtPath(this.getStructure(), elementPath, this.rootPath);

        return assertElementDefinitionAtPath(node, absolutePath);
    }

    // TODO(2026-02-10): DRY this with updateSettingsAtPath via one shared
    // "update settings at element path" helper that accepts merge/replace strategy.
    replaceSettingsAtPath(elementPath: StructurePath, settings: Record<string, unknown>): void {
        this.updateAtPath(elementPath, (node, currentPath) => {
            const definition = assertElementDefinitionAtPath(node, currentPath);
            const settingsAtPath = assertSettingsPatchAtPath(
                settings,
                appendStructurePath(currentPath, 'settings')
            );

            return {
                ...definition,
                settings: settingsAtPath,
            };
        });
    }

    setRequestSettingsEditHandler(
        handler: ((request: ElementSettingsEditRequest) => void) | null
    ): void {
        this.requestSettingsEditHandler = handler;
    }

    requestSettingsEdit(request: ElementSettingsEditRequest): void {
        if (!this.requestSettingsEditHandler) {
            throw new Error('Missing settings edit request handler on ReportStructureController.');
        }

        this.requestSettingsEditHandler(request);
    }

    getHelperAtPath(location: ElementLocation): StructurePathActionHelper {
        return new ReportStructurePathActions(this, location);
    }
}
